import React from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import { SequenceStep, Template } from '../../types/campaign';

interface SequenceStepCardProps {
  step: SequenceStep;
  stepNumber: number;
  onEdit?: (step: SequenceStep) => void;
  onDelete?: (step: SequenceStep) => void;
  canEdit?: boolean;
}

export const SequenceStepCard: React.FC<SequenceStepCardProps> = ({
  step,
  stepNumber,
  onEdit,
  onDelete,
  canEdit = true,
}) => {
  const getStepTypeIcon = () => {
    switch (step.step_type) {
      case 'email':
        return EnvelopeIcon;
      case 'sms':
        return ChatBubbleLeftRightIcon;
      case 'delay':
        return ClockIcon;
      case 'condition':
        return CodeBracketIcon;
      default:
        return ClockIcon;
    }
  };

  const getStepTypeColor = () => {
    switch (step.step_type) {
      case 'email':
        return 'bg-blue-100 text-blue-800';
      case 'sms':
        return 'bg-purple-100 text-purple-800';
      case 'delay':
        return 'bg-yellow-100 text-yellow-800';
      case 'condition':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDelay = () => {
    if (step.step_type === 'delay') {
      const parts: string[] = [];
      if (step.delay_days && step.delay_days > 0) {
        parts.push(`${step.delay_days} day${step.delay_days !== 1 ? 's' : ''}`);
      }
      if (step.delay_hours && step.delay_hours > 0) {
        parts.push(`${step.delay_hours} hour${step.delay_hours !== 1 ? 's' : ''}`);
      }
      return parts.length > 0 ? parts.join(', ') : 'No delay specified';
    }
    return null;
  };

  const Icon = getStepTypeIcon();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold">
              {stepNumber}
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStepTypeColor()}`}>
              <Icon className="w-3 h-3 mr-1" />
              {step.step_type.charAt(0).toUpperCase() + step.step_type.slice(1)}
            </span>
            <h4 className="text-sm font-medium text-gray-900">{step.name}</h4>
          </div>

          {step.step_type === 'email' || step.step_type === 'sms' ? (
            <div className="mt-2">
              {step.template ? (
                <Link
                  to={`/templates/${step.template.id}`}
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  {step.template.name}
                </Link>
              ) : step.template_id ? (
                <span className="text-sm text-gray-500">Template ID: {step.template_id}</span>
              ) : (
                <span className="text-sm text-red-500">No template selected</span>
              )}
            </div>
          ) : step.step_type === 'delay' ? (
            <div className="mt-2">
              <span className="text-sm text-gray-600">{formatDelay()}</span>
            </div>
          ) : step.step_type === 'condition' ? (
            <div className="mt-2">
              <span className="text-sm text-gray-500">
                {step.conditions ? 'Condition configured' : 'No conditions'}
              </span>
            </div>
          ) : null}
        </div>

        {canEdit && (onEdit || onDelete) && (
          <div className="flex items-center space-x-1 ml-4">
            {onEdit && (
              <button
                onClick={() => onEdit(step)}
                className="p-1 text-gray-400 hover:text-indigo-600"
                title="Edit step"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(step)}
                className="p-1 text-gray-400 hover:text-red-600"
                title="Delete step"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

